
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Link } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const QuickSubmitForm = () => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [totalExperience, setTotalExperience] = useState('');
  const [noticePeriod, setNoticePeriod] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [registrationSent, setRegistrationSent] = useState(false);

  const clearForm = () => {
    setEmail('');
    setRole('');
    setCustomRole('');
    setTotalExperience('');
    setNoticePeriod('');
    setFile(null);
    setTermsAccepted(false);
    setUploadProgress(0);
    setRegistrationSent(false);
    
    const fileInput = document.getElementById('resume') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.type !== 'application/pdf') {
        toast.error('Please upload a PDF file');
        return;
      }
      if (selectedFile.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('File size should be less than 5MB');
        return;
      }
      setFile(selectedFile);
    }
  };

  const checkEmailExists = async (email: string) => {
    try {
      const { data, error } = await supabase
        .from('quick_submissions')
        .select('email')
        .eq('email', email)
        .limit(1);

      if (error) {
        console.error('Error checking email:', error);
        throw new Error('Failed to check if email already exists');
      }
      
      return data && data.length > 0;
    } catch (error) {
      console.error('Error checking email:', error);
      throw new Error('Failed to check if email already exists');
    }
  };

  const sendRegistrationLink = async (submissionId: string) => {
    try {
      const response = await fetch(`${window.location.origin}/api/register-quick-submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ submissionId }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        console.error('Error sending registration link:', result);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Failed to send registration link:', error);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const finalRole = role === 'other' ? customRole : role;
    
    if (!file || !email || !finalRole || !termsAccepted) {
      toast.error(termsAccepted ? 'Please fill in all required fields' : 'Please accept the terms and conditions');
      return;
    }

    if (isSubmitting) {
      toast.error('Your submission is already in progress');
      return;
    }

    try {
      setIsSubmitting(true);
      setUploadProgress(10);

      // Check if email already exists
      let emailExists;
      try {
        emailExists = await checkEmailExists(email);
        if (emailExists) {
          toast.error('This email has already been submitted. Please use a different email address.');
          setIsSubmitting(false);
          setUploadProgress(0);
          return;
        }
      } catch (error) {
        console.error('Error checking email existence:', error);
        toast.error('Could not verify if email was already submitted. Please try again later.');
        setIsSubmitting(false);
        setUploadProgress(0);
        return;
      }

      setUploadProgress(30);

      const timestamp = new Date().getTime();
      const filePath = `resumes/${timestamp}-${file.name}`;
      
      let uploadAttempt = 0;
      let uploadSuccess = false;
      let uploadError = null;
      
      while (uploadAttempt < 3 && !uploadSuccess) {
        try {
          uploadAttempt++;
          setUploadProgress(30 + (uploadAttempt * 10));
          
          const { error } = await supabase.storage
            .from('resumes')
            .upload(filePath, file);
          
          if (error) {
            uploadError = error;
            console.error(`Upload attempt ${uploadAttempt} failed:`, error);
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, uploadAttempt - 1) * 1000));
          } else {
            uploadSuccess = true;
            setUploadProgress(70);
          }
        } catch (err) {
          uploadError = err;
          console.error(`Upload attempt ${uploadAttempt} failed with exception:`, err);
        }
      }
      
      if (!uploadSuccess) {
        throw uploadError || new Error('Failed to upload resume after multiple attempts');
      }

      // Now that the file is uploaded, insert the form data
      const { data: submissionData, error: submissionError } = await supabase
        .from('quick_submissions')
        .insert([
          {
            email,
            role: finalRole,
            resume_url: filePath,
            total_experience: totalExperience ? parseInt(totalExperience) : null,
            notice_period: noticePeriod
          }
        ])
        .select('id')
        .single();

      if (submissionError) {
        // Check if this is a duplicate violation (this is a backup check)
        if (submissionError.code === '23505' || submissionError.message?.includes('duplicate')) {
          toast.error('This email has already been submitted. Please use a different email address.');
        } else {
          throw submissionError;
        }
      } else {
        setUploadProgress(100);
        toast.success('Resume submitted successfully!');
        
        // Send registration link for the new submission
        const registrationSent = await sendRegistrationLink(submissionData.id);
        setRegistrationSent(registrationSent);
        
        if (registrationSent) {
          toast.success('Check your email to create an account!', {
            duration: 6000,
            description: 'We\'ve sent you a magic link to create your account and track your application.'
          });
        }
        
        clearForm();
      }
      
    } catch (error: any) {
      console.error('Submission error:', error);
      
      // Customize error message based on error type
      let errorMessage = 'Failed to submit resume. Please try again.';
      
      if (error.message && error.message.includes('already been submitted')) {
        errorMessage = 'This email has already been submitted. Please use a different email address.';
      } else if (error.message && error.message.includes('storage')) {
        errorMessage = 'Failed to upload your resume. Please try again with a different PDF file.';
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const roles = [
    { value: 'software_engineer', label: 'Software Engineer/Developer' },
    { value: 'full_stack_developer', label: 'Full-Stack Developer' },
    { value: 'front_end_developer', label: 'Front-End Developer' },
    { value: 'back_end_developer', label: 'Back-End Developer' },
    { value: 'mobile_developer', label: 'Mobile Application Developer' },
    { value: 'data_scientist', label: 'Data Scientist' },
    { value: 'data_analyst', label: 'Data Analyst' },
    { value: 'cloud_engineer', label: 'Cloud Architect/Engineer' },
    { value: 'devops_engineer', label: 'DevOps Engineer' },
    { value: 'cybersecurity_specialist', label: 'Cybersecurity Specialist/Engineer' },
    { value: 'ai_ml_engineer', label: 'AI/ML Engineer' },
    { value: 'blockchain_developer', label: 'Blockchain Developer/Engineer' },
    { value: 'iot_engineer', label: 'IoT Architect/Engineer' },
    { value: 'database_administrator', label: 'Database Administrator' },
    { value: 'network_engineer', label: 'Network Engineer' },
    { value: 'systems_engineer', label: 'Systems/Infrastructure Engineer' },
    { value: 'software_architect', label: 'Software Architect' },
    { value: 'it_project_manager', label: 'IT Project Manager/Digital Transformation Leader' },
    { value: 'ux_ui_designer', label: 'UX/UI Designer' },
    { value: 'technical_support', label: 'Technical Support/IT Support Engineer' },
    
    { value: 'business_development_manager', label: 'Business Development Manager' },
    { value: 'sales_representative', label: 'Sales Representative' },
    { value: 'sales_manager', label: 'Sales Manager' },
    { value: 'account_manager', label: 'Account Manager' },
    { value: 'marketing_specialist', label: 'Marketing Specialist' },
    { value: 'marketing_manager', label: 'Marketing Manager' },
    { value: 'digital_marketing_specialist', label: 'Digital Marketing Specialist' },
    { value: 'content_creator', label: 'Content Creator' },
    { value: 'social_media_manager', label: 'Social Media Manager' },
    { value: 'brand_manager', label: 'Brand Manager' },
    { value: 'product_manager', label: 'Product Manager' },
    { value: 'project_manager', label: 'Project Manager' },
    { value: 'operations_manager', label: 'Operations Manager' },
    { value: 'human_resources', label: 'Human Resources Specialist' },
    { value: 'recruiting_specialist', label: 'Recruiting Specialist' },
    { value: 'finance_analyst', label: 'Finance Analyst' },
    { value: 'accounting_specialist', label: 'Accounting Specialist' },
    { value: 'customer_success_manager', label: 'Customer Success Manager' },
    { value: 'customer_support_specialist', label: 'Customer Support Specialist' },
    { value: 'executive_assistant', label: 'Executive Assistant' },
    
    { value: 'other', label: 'Other (specify)' },
  ];

  const noticePeriodOptions = [
    { value: 'immediate', label: 'Immediate' },
    { value: '15_days', label: '15 Days' },
    { value: '30_days', label: '30 Days' },
    { value: '60_days', label: '60 Days' },
    { value: '90_days', label: '90 Days' },
    { value: 'custom', label: 'Other (specify)' }
  ];

  return (
    <>
      <CardHeader>
        <CardTitle className="text-black">Quick Resume Submission</CardTitle>
        <CardDescription className="text-gray-600">
          Submit your resume and we'll match you with the right opportunities
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-700">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="border-gray-300 text-black"
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="role" className="text-gray-700">Select your role</Label>
            <Select value={role} onValueChange={setRole} disabled={isSubmitting} required>
              <SelectTrigger className="w-full text-black border-gray-300">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {role === 'other' && (
            <div className="space-y-2">
              <Label htmlFor="customRole" className="text-gray-700">Specify your role</Label>
              <Input
                id="customRole"
                type="text"
                placeholder="Enter your role"
                value={customRole}
                onChange={(e) => setCustomRole(e.target.value)}
                required={role === 'other'}
                className="border-gray-300 text-black"
                disabled={isSubmitting}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="totalExperience" className="text-gray-700">Total Experience (years)</Label>
            <Input
              id="totalExperience"
              type="number"
              min="0"
              max="50"
              placeholder="Enter total years of experience"
              value={totalExperience}
              onChange={(e) => setTotalExperience(e.target.value)}
              className="border-gray-300 text-black"
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="noticePeriod" className="text-gray-700">Notice Period</Label>
            <Select value={noticePeriod} onValueChange={setNoticePeriod} disabled={isSubmitting}>
              <SelectTrigger className="w-full text-black border-gray-300">
                <SelectValue placeholder="Select notice period" />
              </SelectTrigger>
              <SelectContent>
                {noticePeriodOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="resume" className="text-gray-700">Upload Resume (PDF only, max 5MB)</Label>
            <Input
              id="resume"
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              required
              className="border-gray-300 text-black file:bg-[#FEF7CD] file:text-black file:border-0"
              disabled={isSubmitting}
            />
          </div>

          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-in-out" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          )}

          <div className="flex items-start space-x-2">
            <input
              type="checkbox"
              id="terms"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-1"
              disabled={isSubmitting}
            />
            <Label htmlFor="terms" className="text-sm text-gray-600">
              I accept the <Link to="/terms-and-conditions" className="text-[#0EA5E9] hover:underline">Terms and Conditions</Link> 
               and allow Talento AI to use my data to match me with opportunities
            </Label>
          </div>

          <Button 
            type="submit" 
            className="w-full bg-[#FEF7CD] text-black hover:bg-[#FEF7CD]/90" 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Resume'}
          </Button>
        </form>
      </CardContent>
    </>
  );
};

export default QuickSubmitForm;
